import { useEffect, useRef } from "react";
import "./FamilyTreeView.css";
import { logEvent } from "../utils/logEvent";

export default function FamilyTreeView({ members, onSelect, selectedNodeId }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);

  const pan = useRef({ x: 0, y: 0 });
  const zoom = useRef(0.6); // initial zoom

  // ----------------------------
  // DRAW TREE WHEN MEMBERS CHANGE
  // ----------------------------
  useEffect(() => {
    if (!members || members.length === 0) return;

    const positioned = layoutFamilyTree(members);

    const svg = svgRef.current;
    renderFamilyTree(positioned, members, svg, onSelect, selectedNodeId);

    // initial zoom
    svg.style.transformOrigin = "50% 0";
    svg.style.transform = `translate(${pan.current.x}px, ${pan.current.y}px) scale(${zoom.current})`;
  }, [members, selectedNodeId, onSelect]);

  // ----------------------------
  // ZOOM + BUTTON CONTROLS
  // ----------------------------
  useEffect(() => {
  const svg = svgRef.current;
  if (!svg) return;

  function applyTransform() {
    svg.style.transform = `translate(${pan.current.x}px, ${pan.current.y}px) scale(${zoom.current})`;
  }

  const zoomInHandler = async () => { zoom.current *= 1.1; applyTransform(); await logEvent("zoom_in_executed"); };
  const zoomOutHandler = async () => { zoom.current /= 1.1; applyTransform(); await logEvent("zoom_out_executed"); };
  const panLeftHandler = async () => { pan.current.x += 40; applyTransform(); await logEvent("pan_left_executed"); };
  const panRightHandler = async () => { pan.current.x -= 40; applyTransform(); await logEvent("pan_right_executed"); };
  const panUpHandler = async () => { pan.current.y += 40; applyTransform(); await logEvent("pan_up_executed"); };
  const panDownHandler = async () => { pan.current.y -= 40; applyTransform(); await logEvent("pan_down_executed"); };

  window.addEventListener("zoom_in", zoomInHandler);
  window.addEventListener("zoom_out", zoomOutHandler);
  window.addEventListener("pan_left", panLeftHandler);
  window.addEventListener("pan_right", panRightHandler);
  window.addEventListener("pan_up", panUpHandler);
  window.addEventListener("pan_down", panDownHandler);

  // ----------------------------
  // DRAG ANYWHERE IN CONTAINER
  // ----------------------------
  const container = containerRef.current;
  let isDragging = false;
  let start = { x: 0, y: 0 };

  const mouseDownHandler = (e) => { isDragging = true; start = { x: e.clientX, y: e.clientY }; };
  const mouseMoveHandler = (e) => {
    if (!isDragging) return;
    pan.current.x += e.clientX - start.x;
    pan.current.y += e.clientY - start.y;
    start = { x: e.clientX, y: e.clientY };
    applyTransform();
  };
  const mouseUpHandler = () => { isDragging = false; };

  container.addEventListener("mousedown", mouseDownHandler);
  window.addEventListener("mousemove", mouseMoveHandler);
  window.addEventListener("mouseup", mouseUpHandler);

  // Cleanup on unmount or re-run
  return () => {
    window.removeEventListener("zoom_in", zoomInHandler);
    window.removeEventListener("zoom_out", zoomOutHandler);
    window.removeEventListener("pan_left", panLeftHandler);
    window.removeEventListener("pan_right", panRightHandler);
    window.removeEventListener("pan_up", panUpHandler);
    window.removeEventListener("pan_down", panDownHandler);

    container.removeEventListener("mousedown", mouseDownHandler);
    window.removeEventListener("mousemove", mouseMoveHandler);
    window.removeEventListener("mouseup", mouseUpHandler);
  };
}, []);

  return (
    <div className="family-tree-container" ref={containerRef}>
      <svg ref={svgRef} className="family-tree-svg">
        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="10"
            refX="9"
            refY="3"
            orient="auto"
          >
            <polygon points="0 0, 10 3, 0 6" fill="#999" />
          </marker>
        </defs>
      </svg>
    </div>
  );
}

/* ============================================================
   TREE LAYOUT
============================================================ */
function layoutFamilyTree(members) {
  const nodeWidth = 120;
  const nodeHeight = 60;
  const horizontalSpacing = 40;
  const verticalSpacing = 100;

  const generations = assignGenerations(members);

  const byGeneration = {};
  members.forEach((m) => {
    const gen = generations[m.id];
    if (!byGeneration[gen]) byGeneration[gen] = [];
    byGeneration[gen].push(m);
  });

  const coParents = findCoParentGroups(members);

  Object.keys(byGeneration).forEach((g) => {
    byGeneration[g] = smartSort(byGeneration[g], members, coParents);
  });

  const positioned = {};
  const gens = Object.keys(byGeneration).sort((a, b) => Number(a) - Number(b));

  gens.forEach((gen, i) => {
    const row = byGeneration[gen];
    const y = i * (nodeHeight + verticalSpacing) - 100; // SHIFT UP

    const totalWidth =
      row.length * nodeWidth + (row.length - 1) * horizontalSpacing;
    let startX = -totalWidth / 2;

    row.forEach((m, idx) => {
      const x = startX + idx * (nodeWidth + horizontalSpacing) + nodeWidth / 2;
      positioned[m.id] = { ...m, x, y };
    });
  });

  return positioned;
}

/* ============================================================
   RENDER TREE
============================================================ */
function renderFamilyTree(positioned, members, svg, onSelect, selectedNodeId) {
  if (!svg) return;

  // Clear svg (keep defs)
  while (svg.lastChild && svg.lastChild.tagName !== "defs") {
    svg.removeChild(svg.lastChild);
  }

  const nodes = Object.values(positioned);
  if (nodes.length === 0) return;

  // Compute viewBox
  const xs = nodes.map((n) => n.x);
  const ys = nodes.map((n) => n.y);
  const minX = Math.min(...xs) - 150;
  const maxX = Math.max(...xs) + 150;
  const minY = Math.min(...ys) - 50;
  const maxY = Math.max(...ys) + 80;

  svg.setAttribute("viewBox", `${minX} ${minY} ${maxX - minX} ${maxY - minY}`);

  /* ---- Draw edges ---- */
  members.forEach((member) => {
    const childNode = positioned[member.id];
    if (!childNode) return;

    if (member.parents) {
      member.parents.forEach((parentId) => {
        const parentNode = positioned[parentId];
        if (!parentNode) return;

        const path = document.createElementNS("http://www.w3.org/2000/svg", "path");

        const x1 = parentNode.x;
        const y1 = parentNode.y + 30;
        const x2 = childNode.x;
        const y2 = childNode.y - 30;

        const cy = (y1 + y2) / 2;

        path.setAttribute(
          "d",
          `M ${x1} ${y1} C ${x1} ${cy}, ${x2} ${cy}, ${x2} ${y2}`
        );
        path.setAttribute("stroke", "#999");
        path.setAttribute("stroke-width", "2");
        path.setAttribute("fill", "none");
        path.setAttribute("marker-end", "url(#arrowhead)");

        svg.appendChild(path);
      });
    }
  });

  /* ---- Draw nodes ---- */
  nodes.forEach((node) => {
    const g = document.createElementNS("http://www.w3.org/2000/svg", "g");
    g.style.cursor = "pointer";

    const selected = node.id === selectedNodeId;

    // box
    const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    rect.setAttribute("x", node.x - 60);
    rect.setAttribute("y", node.y - 30);
    rect.setAttribute("width", "120");
    rect.setAttribute("height", "60");
    rect.setAttribute("rx", "8");
    rect.setAttribute("fill", selected ? "#4a90e2" : "#fff");
    rect.setAttribute("stroke", selected ? "#2c5aa0" : "#ddd");
    rect.setAttribute("stroke-width", selected ? "3" : "2");

    // name
    const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
    text.setAttribute("x", node.x);
    text.setAttribute("y", node.y + 5);
    text.setAttribute("text-anchor", "middle");
    text.setAttribute("fill", selected ? "#fff" : "#333");
    text.setAttribute("font-size", "14");
    text.textContent = node.name;

    // events
    g.addEventListener("click", (e) => {
      e.stopPropagation();
      onSelect(node);
    });

    g.addEventListener("dblclick", async () => {

      await logEvent("view_profile_executed_node_dblclick", {
        selected_id: node.id,
        selected_name: node.name
      });

      window.location.href = `/member/${node.id}`;
    });

    // memory popup
    const mem = document.createElementNS("http://www.w3.org/2000/svg", "text");
    mem.textContent = "📝";
    mem.setAttribute("x", node.x + 40);
    mem.setAttribute("y", node.y - 10);
    mem.setAttribute("font-size", "30");
    mem.setAttribute("cursor", "pointer");
    mem.setAttribute("visibility", "hidden");

    mem.addEventListener("click", async (e) => {
      e.stopPropagation();

      await logEvent("view_profile_executed_node_click", {
        selected_id: node.id,
        selected_name: node.name
      });

      window.location.href = `/member/${node.id}#add-memory`;
    });

    g.addEventListener("mouseenter", () => {
      mem.setAttribute("visibility", "visible");
    });
    g.addEventListener("mouseleave", () => {
      mem.setAttribute("visibility", "hidden");
    });

    g.appendChild(rect);
    g.appendChild(text);
    g.appendChild(mem);

    svg.appendChild(g);
  });
}

/* ============================================================
   UTILS (co-parents, sorting, generations)
============================================================ */
function findCoParentGroups(members) {
  const groups = [];
  const used = new Set();

  members.forEach((child) => {
    if (!child.parents || child.parents.length < 2) return;

    const key = [...child.parents].sort().join(",");
    if (!used.has(key)) {
      used.add(key);
      groups.push({ parents: child.parents, children: [child.id] });
    }
  });

  return groups;
}

function smartSort(membersInGen) {
  return membersInGen; // simple, stable
}

function assignGenerations(members) {
  const gen = {};
  const roots = members.filter((m) => !m.parents || m.parents.length === 0);
  roots.forEach((r) => (gen[r.id] = 0));

  let changed = true;
  while (changed) {
    changed = false;
    members.forEach((m) => {
      if (gen[m.id] !== undefined) return;
      if (!m.parents || m.parents.some((p) => gen[p] === undefined)) return;

      gen[m.id] = Math.max(...m.parents.map((p) => gen[p])) + 1;
      changed = true;
    });
  }

  return gen;
}
